import { useCallback, useEffect, useState } from "react";

// Scale disponibili per il testo dei messaggi. Il ciclo e' voluto: un solo
// pulsante "Aa" che passa da una all'altra, senza aprire menu.
export const CHAT_FONT_STEPS = ["sm", "md", "lg", "xl"] as const;
export type ChatFontStep = (typeof CHAT_FONT_STEPS)[number];

const STORAGE_KEY = "chat_font_size";
// Evento custom: tiene allineate piu' finestre di chat aperte insieme, che
// altrimenti manterrebbero ognuna il proprio stato locale.
const SYNC_EVENT = "cercamanti:chat-font-size";

// Classi Tailwind per il testo dei messaggi, una per scala.
const BUBBLE_CLASS: Record<ChatFontStep, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-xl",
};

function readStored(): ChatFontStep {
  if (typeof window === "undefined") return "sm";
  const raw = localStorage.getItem(STORAGE_KEY);
  return CHAT_FONT_STEPS.includes(raw as ChatFontStep) ? (raw as ChatFontStep) : "sm";
}

/**
 * Dimensione del testo nelle chat, condivisa tra la finestra desktop e la
 * pagina chat mobile e persistita tra le sessioni.
 */
export function useChatFontSize() {
  const [step, setStep] = useState<ChatFontStep>(readStored);

  // Allinea le altre finestre di chat aperte (stesso tab) e gli altri tab.
  useEffect(() => {
    const onSync = () => setStep(readStored());
    window.addEventListener(SYNC_EVENT, onSync);
    window.addEventListener("storage", onSync);
    return () => {
      window.removeEventListener(SYNC_EVENT, onSync);
      window.removeEventListener("storage", onSync);
    };
  }, []);

  const cycleFontSize = useCallback(() => {
    setStep((current) => {
      const next =
        CHAT_FONT_STEPS[(CHAT_FONT_STEPS.indexOf(current) + 1) % CHAT_FONT_STEPS.length];
      localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent(SYNC_EVENT));
      return next;
    });
  }, []);

  return {
    fontStep: step,
    // Classe da applicare al testo dei messaggi
    bubbleTextClass: BUBBLE_CLASS[step],
    cycleFontSize,
    // Per l'aria-label / title del pulsante
    isDefaultSize: step === "sm",
  };
}
